/**
 * ShadowPass.ts — renders a sun-perspective depth map of the terrain.
 *
 * Output: 2048×2048 depth32float texture. Reverse-Z (matches main render).
 *
 * Uses an orthographic projection covering the basin. The terrain mesh is
 * shared with TerrainRenderPass (same index buffer, same heightmap, same
 * procedural vertex grid built from vertex_index). A dedicated, minimal
 * vertex-only shader (`terrain_shadow.wgsl`) avoids touching the main camera
 * uniform.
 *
 * Updated each frame: lightViewProj is rebuilt from the current sun direction.
 *
 * Stage 2 only — water is NOT cast into the shadow map (waves change every
 * frame; sampling shadow on water is also fine for self-occlusion later).
 */

import shadowWgsl from "../shaders/terrain_shadow.wgsl?raw";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import type { ShadowUniforms } from "../rendering/ShadowUniforms";
import { REVERSE_Z_COMPARE } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import type { TerrainRenderPass } from "./TerrainRenderPass";

/** Anything that can render itself depth-only into the shadow map. */
export interface ShadowCaster {
  encodeShadow(pass: GPURenderPassEncoder): void;
}

export class ShadowPass implements RenderPass {
  readonly name = "ShadowPass";

  private pipeline: GPURenderPipeline;
  private bindGroup: GPUBindGroup;
  private depthTex: GPUTexture;
  /** Sampled view (depth aspect) for downstream passes. */
  readonly depthView: GPUTextureView;
  /** Comparison sampler (PCF) for downstream passes. */
  readonly compareSampler: GPUSampler;
  /** Bind-group layout that downstream shaders use as @group(1):
   *    binding 0: Shadow uniform
   *    binding 1: depth texture (sampled)
   *    binding 2: comparison sampler  */
  readonly consumerLayout: GPUBindGroupLayout;
  readonly consumerBindGroup: GPUBindGroup;
  /** Extra depth-only casters (boats, static meshes) drawn after the terrain. */
  private casters: ShadowCaster[] = [];

  /** Register an extra caster. Order is preserved. */
  addCaster(caster: ShadowCaster): void {
    this.casters.push(caster);
  }

  constructor(
    private device: GPUDevice,
    private terrainPass: TerrainRenderPass,
    private lighting: LightingUniforms,
    private shadow: ShadowUniforms,
    consumerLayout: GPUBindGroupLayout,
  ) {
    const N = shadow.resolution;
    this.depthTex = device.createTexture({
      label: "ShadowPass.depth",
      size: { width: N, height: N },
      format: "depth32float",
      usage:
        GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.depthView = this.depthTex.createView({
      label: "ShadowPass.depth.view",
      aspect: "depth-only",
    });
    this.compareSampler = device.createSampler({
      label: "ShadowPass.compareSampler",
      magFilter: "linear",
      minFilter: "linear",
      compare: "greater-equal", // reverse-Z: fragment depth > shadow depth → visible
    });

    const module = device.createShaderModule({
      label: "terrain_shadow.wgsl",
      code: shadowWgsl,
    });
    const bgl = device.createBindGroupLayout({
      label: "ShadowPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.VERTEX,
          texture: { sampleType: "unfilterable-float" },
        },
      ],
    });
    this.pipeline = device.createRenderPipeline({
      label: "ShadowPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
      vertex: { module, entryPoint: "vs_main" },
      // No fragment stage: depth-only render.
      primitive: { topology: "triangle-list", cullMode: "front" },
      depthStencil: {
        format: "depth32float",
        depthWriteEnabled: true,
        depthCompare: REVERSE_Z_COMPARE,
        // Reverse-Z: caster depth in the shadow map should be slightly
        // FARTHER (smaller) than the lit surface to avoid self-shadowing.
        // depthBiasSlopeScale dominates on grazing surfaces (like a deck
        // lit by a low sun), where flat constant bias leaves visible
        // diagonal stripes ("shadow acne") aligned with shadow-map texels.
        // Bumped aggressively to suppress sub-texel acne on planked deck
        // (planks are 0.13 m vs 0.34 m / texel at 2048² × 700 m extent).
        depthBias: -8,
        depthBiasSlopeScale: -8,
        depthBiasClamp: -0.005,
      },
    });

    this.bindGroup = device.createBindGroup({
      label: "ShadowPass.bg",
      layout: bgl,
      entries: [
        { binding: 0, resource: { buffer: shadow.buffer } },
        { binding: 1, resource: { buffer: terrainPass.terrainUniformBuffer } },
        { binding: 2, resource: terrainPass.heightmapView() },
      ],
    });

    // Layout / bind group used by every consumer shader at @group(1).
    this.consumerLayout = consumerLayout;
    this.consumerBindGroup = device.createBindGroup({
      label: "ShadowConsumer.bg",
      layout: this.consumerLayout,
      entries: [
        { binding: 0, resource: { buffer: shadow.buffer } },
        { binding: 1, resource: this.depthView },
        { binding: 2, resource: this.compareSampler },
      ],
    });
  }

  encode(encoder: GPUCommandEncoder, _frame: FrameContext): void {
    // Recompute light VP from the current sun direction. The lighting buffer
    // already stores it; we mirror the same azel computation locally to avoid
    // GPU readback.
    const az = this.lighting.state.sunAzimuth;
    const el = this.lighting.state.sunElevation;
    const ce = Math.cos(el);
    const sunDir: [number, number, number] = [
      Math.sin(az) * ce,
      Math.sin(el),
      -Math.cos(az) * ce,
    ];
    this.shadow.update(this.device, sunDir);

    const pass = encoder.beginRenderPass({
      label: "ShadowPass",
      colorAttachments: [],
      depthStencilAttachment: {
        view: this.depthView,
        depthClearValue: 0.0, // reverse-Z: far = 0
        depthLoadOp: "clear",
        depthStoreOp: "store",
      },
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.setIndexBuffer(this.terrainPass.indexBufferRef, "uint32");
    pass.drawIndexed(this.terrainPass.indexCountRef, 1, 0, 0, 0);
    for (const c of this.casters) c.encodeShadow(pass);
    pass.end();
  }

  destroy(): void {
    this.depthTex.destroy();
  }
}
